import { Package, PackageGroupSize } from '../types/Package';

export type PackageTemplate = Omit<
  Package,
  | 'id'
  | 'slug'
  | 'order'
  | 'updatedAt'
  | 'available'
  | 'featured'
  | 'image'
  | 'heroImage'
  | 'galleryImages'
  | 'customization'
> & {
  templateKey: string;
};

// Helper function to convert duration string to minutes
function parseDuration(duration: string): number {
  const lower = duration.toLowerCase().trim();
  
  if (lower.includes('session')) {
    const match = lower.match(/(\d+)\s*sessions?/);
    if (match) return parseInt(match[1]) * 120; // 2 hours per session
  }
  
  if (lower.includes('day')) {
    const match = lower.match(/(\d+(?:\.\d+)?)\s*days?/);
    if (match) return Math.round(parseFloat(match[1]) * 24 * 60);
  }
  
  if (lower.includes('hour')) {
    const match = lower.match(/(\d+(?:\.\d+)?)\s*hours?/);
    if (match) return Math.round(parseFloat(match[1]) * 60);
  }
  
  if (lower.includes('minute')) {
    const match = lower.match(/(\d+)\s*minutes?/);
    if (match) return parseInt(match[1]);
  }
  
  return 0;
}

// Helper function to create template key from package name
function createTemplateKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Helper function to parse add-ons
function parseAddons(addonsStr: string): Array<{ name: string; price?: number }> {
  if (!addonsStr || !addonsStr.trim()) return [];
  return addonsStr.split(';').map(addon => ({ name: addon.trim() }));
}

// Helper function to parse group size from CSV
export function parseGroupSize(groupSizeStr: string): PackageGroupSize | undefined {
  if (!groupSizeStr) return undefined;
  
  const size = groupSizeStr.trim().toLowerCase();
  if (['solo', 'small', 'medium', 'large', 'verylarge', 'any'].includes(size)) {
    return size as PackageGroupSize;
  }
  
  return undefined;
}

// Creative category-specific templates with multiple duration options
export const PACKAGE_TEMPLATES: PackageTemplate[] = [
  // ============================================
  // PORTRAIT CATEGORY
  // ============================================
  {
    templateKey: 'portrait-essence-1hr',
    name: 'Essence Portrait',
    category: 'Portrait',
    short: 'A focused hour capturing your authentic self.',
    description: 'A beautifully intimate one-hour portrait session designed to capture your essence. Perfect for professional headshots, LinkedIn profiles, or personal portraits that tell your story. I\'ll work with you to find your best angles and expressions in a relaxed, comfortable environment. This session is ideal for those who want stunning, professional-quality images without the time commitment of a longer shoot.',
    price: 2500,
    currency: 'R',
    durationMinutes: 60,
    groupSize: 'solo',
    isSpecial: false,
  },
  {
    templateKey: 'portrait-story-2hr',
    name: 'Portrait Story',
    category: 'Portrait',
    short: 'Extended session telling your visual story with multiple looks.',
    description: 'A two-hour portrait experience that goes beyond a single shot. Perfect for engagement sessions, professional branding, or capturing multiple facets of your personality. This extended time allows for outfit changes, location variations, and a diverse collection of images. Whether you\'re building your personal brand or creating a gallery of meaningful portraits, this session provides the flexibility and variety you need.',
    price: 4500,
    currency: 'R',
    durationMinutes: 120,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'portrait-collection-3hr',
    name: 'Portrait Collection',
    category: 'Portrait',
    short: 'Comprehensive portrait session with multiple setups and locations.',
    description: 'A three-hour portrait collection session that captures you from every angle. Ideal for detailed portrait work, professional portfolios, or creating a comprehensive visual narrative. This extended duration allows for multiple setups, location changes, and a relaxed pace that ensures I capture every important detail. Perfect for clients who want a thorough, unhurried portrait experience with plenty of variety.',
    price: 6500,
    currency: 'R',
    durationMinutes: 180,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'portrait-premium-4hr',
    name: 'Premium Portrait Experience',
    category: 'Portrait',
    short: 'Luxury portrait session with extensive styling and multiple locations.',
    description: 'A premium four-hour portrait experience designed for those who want the very best. This comprehensive session includes extensive styling, multiple wardrobe changes, and the flexibility to explore different locations and moods. Perfect for larger family portraits, detailed commercial work, or creating an extensive portrait collection. This duration ensures I have time to perfect every detail and capture images that truly reflect your vision.',
    price: 8500,
    currency: 'R',
    durationMinutes: 240,
    groupSize: 'medium',
    isSpecial: false,
  },
  {
    templateKey: 'portrait-luxury-6hr',
    name: 'Luxury Portrait Day',
    category: 'Portrait',
    short: 'Full-day portrait experience with complete creative freedom.',
    description: 'A full six-hour luxury portrait day offering complete creative freedom. This extensive session is perfect for large family reunions, comprehensive commercial projects, or creating an extensive portrait gallery. With multiple locations, extensive styling changes, and thorough coverage, I\'ll create a diverse collection of images that tells your complete story. Ideal for clients who want comprehensive documentation with plenty of time for creative exploration.',
    price: 12000,
    currency: 'R',
    durationMinutes: 360,
    groupSize: 'large',
    isSpecial: false,
  },
  {
    templateKey: 'portrait-elite-8hr',
    name: 'Elite Portrait Experience',
    category: 'Portrait',
    short: 'Ultimate full-day portrait coverage with unlimited creative possibilities.',
    description: 'The ultimate eight-hour portrait experience for those who demand excellence. This comprehensive full-day session provides unlimited creative possibilities with extensive styling, multiple locations, and complete coverage. Perfect for large events, extensive commercial projects, or creating a complete visual narrative. This duration ensures nothing is missed, with time for travel, setup changes, and capturing every important moment in stunning detail.',
    price: 15000,
    currency: 'R',
    durationMinutes: 480,
    groupSize: 'large',
    isSpecial: false,
  },

  // ============================================
  // WEDDING CATEGORY
  // ============================================
  {
    templateKey: 'wedding-intimate-1hr',
    name: 'Intimate Moments',
    category: 'Wedding',
    short: 'Focused coverage of your most precious wedding moments.',
    description: 'A beautifully focused one-hour wedding session capturing your most intimate moments. Perfect for elopements, intimate ceremonies, or capturing specific moments like your first look or private vows. This session focuses on the emotional connection between you and your partner, creating timeless images that capture the essence of your love story in a relaxed, unobtrusive way.',
    price: 3500,
    currency: 'R',
    durationMinutes: 60,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'wedding-essentials-2hr',
    name: 'Wedding Essentials',
    category: 'Wedding',
    short: 'Complete coverage of your ceremony and key moments.',
    description: 'A comprehensive two-hour wedding package covering your ceremony and all essential moments. From your final preparations to the first dance, I\'ll capture the joy, tears, and laughter that make your day unforgettable. This package is perfect for couples who want beautiful coverage of the ceremony, family portraits, and key reception highlights without an oversized production crew.',
    price: 5500,
    currency: 'R',
    durationMinutes: 120,
    groupSize: 'large',
    isSpecial: false,
  },
  {
    templateKey: 'wedding-complete-3hr',
    name: 'Complete Wedding Story',
    category: 'Wedding',
    short: 'Full wedding day coverage from preparations to celebration.',
    description: 'A complete three-hour wedding story that captures your entire celebration. From the quiet anticipation of getting ready to the joy of your reception, I document your day with a photojournalistic approach. This package captures both the big moments and the small details that make your wedding unique, creating a complete visual story that preserves not just what happened, but how it felt.',
    price: 7500,
    currency: 'R',
    durationMinutes: 180,
    groupSize: 'large',
    isSpecial: false,
  },
  {
    templateKey: 'wedding-premium-4hr',
    name: 'Premium Wedding Coverage',
    category: 'Wedding',
    short: 'Extended coverage with multiple locations and detailed documentation.',
    description: 'A premium four-hour wedding package offering extended coverage with multiple locations and detailed documentation. Perfect for couples who want comprehensive coverage of their ceremony, portraits, and reception with time for creative exploration. This duration allows for travel between locations, extensive family portraits, and capturing every important moment in beautiful detail.',
    price: 9500,
    currency: 'R',
    durationMinutes: 240,
    groupSize: 'large',
    isSpecial: false,
  },
  {
    templateKey: 'wedding-luxury-6hr',
    name: 'Luxury Wedding Day',
    category: 'Wedding',
    short: 'Full-day wedding coverage with complete creative documentation.',
    description: 'A luxury six-hour wedding day package providing complete creative documentation of your celebration. This extensive coverage captures everything from early morning preparations to evening celebrations, ensuring no moment is missed. Perfect for couples who want comprehensive coverage with multiple locations, extensive styling, and a complete visual narrative of their special day.',
    price: 13000,
    currency: 'R',
    durationMinutes: 360,
    groupSize: 'large',
    isSpecial: false,
  },
  {
    templateKey: 'wedding-elite-8hr',
    name: 'Elite Wedding Experience',
    category: 'Wedding',
    short: 'Ultimate full-day wedding coverage from dawn to dusk.',
    description: 'The ultimate eight-hour wedding experience providing complete coverage from dawn to dusk. This comprehensive package ensures thorough documentation of every moment, from early morning preparations to the final dance. With multiple locations, extensive styling, and complete creative freedom, I\'ll create a stunning visual story that captures the magic, emotion, and beauty of your entire wedding day.',
    price: 16000,
    currency: 'R',
    durationMinutes: 480,
    groupSize: 'large',
    isSpecial: false,
  },

  // ============================================
  // COMMERCIAL / BUSINESS CATEGORY
  // ============================================
  {
    templateKey: 'commercial-express-1hr',
    name: 'Express Business Session',
    category: 'Commercial',
    short: 'Quick, professional business photography for busy professionals.',
    description: 'An efficient one-hour business photography session designed for busy professionals. Perfect for headshots, LinkedIn photos, or quick product shots. This focused session delivers professional-quality images with minimal time investment, ideal for executives, entrepreneurs, and professionals who need quality business photography without taking half a day out of their schedule.',
    price: 3000,
    currency: 'R',
    durationMinutes: 60,
    groupSize: 'solo',
    isSpecial: false,
  },
  {
    templateKey: 'commercial-branding-2hr',
    name: 'Branding Photography',
    category: 'Commercial',
    short: 'Professional branding session for your business or personal brand.',
    description: 'A comprehensive two-hour branding photography session designed to elevate your professional image. Perfect for business leaders, entrepreneurs, and creatives building their personal brand. This session includes multiple looks, settings, and styles to create a cohesive visual identity across your website, social media, and marketing materials. Ideal for those who want professional images that convey confidence and authenticity.',
    price: 5000,
    currency: 'R',
    durationMinutes: 120,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'commercial-corporate-3hr',
    name: 'Corporate Photography',
    category: 'Commercial',
    short: 'Comprehensive corporate photography for teams and products.',
    description: 'A three-hour corporate photography session covering team portraits, product photography, and office documentation. Perfect for companies needing professional images for websites, annual reports, or marketing materials. This extended duration allows for multiple setups, team combinations, and detailed product shots, ensuring comprehensive coverage of all your corporate photography needs.',
    price: 7000,
    currency: 'R',
    durationMinutes: 180,
    groupSize: 'medium',
    isSpecial: false,
  },
  {
    templateKey: 'commercial-premium-4hr',
    name: 'Premium Commercial Shoot',
    category: 'Commercial',
    short: 'Extended commercial photography with multiple setups and locations.',
    description: 'A premium four-hour commercial photography session offering extensive coverage with multiple setups and locations. Perfect for larger commercial projects requiring detailed product photography, team portraits, and location documentation. This duration provides flexibility for travel between locations, extensive styling, and capturing a wide variety of images for comprehensive marketing campaigns.',
    price: 9000,
    currency: 'R',
    durationMinutes: 240,
    groupSize: 'medium',
    isSpecial: false,
  },
  {
    templateKey: 'commercial-luxury-6hr',
    name: 'Luxury Commercial Day',
    category: 'Commercial',
    short: 'Full-day commercial photography with complete project coverage.',
    description: 'A full six-hour luxury commercial photography day providing complete project coverage. Perfect for large-scale commercial projects, extensive product catalogs, or comprehensive brand documentation. This extended duration allows for multiple locations, extensive styling changes, and thorough coverage of all project requirements, ensuring every detail is captured to the highest standard.',
    price: 12500,
    currency: 'R',
    durationMinutes: 360,
    groupSize: 'large',
    isSpecial: false,
  },
  {
    templateKey: 'commercial-elite-8hr',
    name: 'Elite Commercial Experience',
    category: 'Commercial',
    short: 'Ultimate full-day commercial photography with unlimited coverage.',
    description: 'The ultimate eight-hour commercial photography experience offering unlimited coverage for your most important projects. This comprehensive full-day session ensures complete documentation of large-scale commercial projects, extensive product lines, or comprehensive brand campaigns. With multiple locations, extensive styling, and complete creative freedom, I\'ll deliver a complete visual solution for your commercial needs.',
    price: 16000,
    currency: 'R',
    durationMinutes: 480,
    groupSize: 'large',
    isSpecial: false,
  },

  // ============================================
  // FAMILY CATEGORY
  // ============================================
  {
    templateKey: 'family-mini-1hr',
    name: 'Family Mini Session',
    category: 'Family',
    short: 'Quick, fun family session capturing your family\'s personality.',
    description: 'A delightful one-hour family mini session perfect for capturing your family\'s personality and connection. Ideal for small families, quick updates, or seasonal portraits. This focused session creates beautiful, natural images of your family together in a relaxed, comfortable environment. Perfect for busy families who want quality family portraits without a long time commitment.',
    price: 2800,
    currency: 'R',
    durationMinutes: 60,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'family-classic-2hr',
    name: 'Classic Family Portrait',
    category: 'Family',
    short: 'Extended family session with multiple poses and locations.',
    description: 'A beautiful two-hour classic family portrait session offering multiple poses, locations, and outfit changes. Perfect for families who want variety and flexibility in their session. This extended duration allows for individual portraits, group combinations, and candid moments that capture the unique dynamics of your family. Ideal for creating a comprehensive family portrait collection.',
    price: 4800,
    currency: 'R',
    durationMinutes: 120,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'family-extended-3hr',
    name: 'Extended Family Session',
    category: 'Family',
    short: 'Comprehensive family photography with extended coverage.',
    description: 'A comprehensive three-hour extended family session perfect for larger families or those wanting extensive coverage. This session includes multiple locations, outfit changes, and detailed portraits of family members individually and in various combinations. Perfect for creating a complete family portrait collection that captures every relationship and dynamic within your family.',
    price: 6800,
    currency: 'R',
    durationMinutes: 180,
    groupSize: 'medium',
    isSpecial: false,
  },
  {
    templateKey: 'family-gathering-4hr',
    name: 'Family Gathering',
    category: 'Family',
    short: 'Full family gathering coverage with multiple generations.',
    description: 'A four-hour family gathering session designed for larger family reunions or multi-generational portraits. This comprehensive coverage ensures every family member is captured individually and in various group combinations. Perfect for extended families who want thorough documentation of their gathering with time for relaxed, natural moments and formal portraits.',
    price: 8800,
    currency: 'R',
    durationMinutes: 240,
    groupSize: 'large',
    isSpecial: false,
  },
  {
    templateKey: 'family-reunion-6hr',
    name: 'Family Reunion',
    category: 'Family',
    short: 'Full-day family reunion coverage with complete documentation.',
    description: 'A full six-hour family reunion package providing complete documentation of your special gathering. This extensive coverage captures everything from individual portraits to large group shots, ensuring every family member and relationship is beautifully documented. Perfect for large family reunions where you want comprehensive coverage with plenty of time for relaxed, natural moments.',
    price: 12000,
    currency: 'R',
    durationMinutes: 360,
    groupSize: 'large',
    isSpecial: false,
  },
  {
    templateKey: 'family-celebration-8hr',
    name: 'Family Celebration',
    category: 'Family',
    short: 'Ultimate full-day family celebration coverage.',
    description: 'The ultimate eight-hour family celebration package providing complete coverage of your special day. This comprehensive full-day session ensures thorough documentation of large family gatherings, celebrations, or reunions. With multiple locations, extensive styling, and complete coverage, I\'ll create a complete visual story that captures every moment, relationship, and memory of your family celebration.',
    price: 15000,
    currency: 'R',
    durationMinutes: 480,
    groupSize: 'verylarge',
    isSpecial: false,
  },

  // ============================================
  // MATERNITY CATEGORY
  // ============================================
  {
    templateKey: 'maternity-intimate-1hr',
    name: 'Intimate Maternity',
    category: 'Maternity',
    short: 'Beautiful maternity session capturing this special time.',
    description: 'A beautiful one-hour intimate maternity session designed to celebrate this special time in your journey. Perfect for capturing the beauty and emotion of pregnancy in a relaxed, comfortable environment. This focused session creates stunning images that honor this unique moment, whether you prefer natural, lifestyle images or more styled portraits.',
    price: 3000,
    currency: 'R',
    durationMinutes: 60,
    groupSize: 'solo',
    isSpecial: false,
  },
  {
    templateKey: 'maternity-elegant-2hr',
    name: 'Elegant Maternity',
    category: 'Maternity',
    short: 'Extended maternity session with multiple looks and styles.',
    description: 'An elegant two-hour maternity session offering multiple looks, styles, and settings. Perfect for expecting mothers who want variety in their maternity portraits. This extended duration allows for outfit changes, different poses, and both intimate and styled images. Whether you prefer natural light or studio settings, I\'ll create a beautiful collection that celebrates this special time.',
    price: 5000,
    currency: 'R',
    durationMinutes: 120,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'maternity-complete-3hr',
    name: 'Complete Maternity Experience',
    category: 'Maternity',
    short: 'Comprehensive maternity session with partner and styling.',
    description: 'A complete three-hour maternity experience including partner portraits, multiple looks, and extensive styling. This comprehensive session captures not just your pregnancy, but the connection between you and your partner during this special time. Perfect for couples who want a thorough maternity portrait collection with time for relaxed, natural moments and styled portraits.',
    price: 7000,
    currency: 'R',
    durationMinutes: 180,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'maternity-premium-4hr',
    name: 'Premium Maternity Collection',
    category: 'Maternity',
    short: 'Luxury maternity session with extensive styling and locations.',
    description: 'A premium four-hour maternity collection offering extensive styling, multiple locations, and complete creative freedom. This luxury session is perfect for expecting mothers who want a comprehensive portrait collection celebrating their pregnancy. With multiple outfit changes, location variations, and detailed styling, I\'ll create a stunning gallery that beautifully documents this special time.',
    price: 9000,
    currency: 'R',
    durationMinutes: 240,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'maternity-luxury-6hr',
    name: 'Luxury Maternity Day',
    category: 'Maternity',
    short: 'Full-day maternity experience with unlimited creative possibilities.',
    description: 'A full six-hour luxury maternity day offering unlimited creative possibilities. This extensive session includes extensive styling, multiple locations, partner portraits, and complete coverage of your maternity journey. Perfect for expecting mothers who want a comprehensive, unhurried portrait experience with plenty of time for creative exploration and capturing every beautiful moment.',
    price: 12500,
    currency: 'R',
    durationMinutes: 360,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'maternity-elite-8hr',
    name: 'Elite Maternity Experience',
    category: 'Maternity',
    short: 'Ultimate full-day maternity coverage with complete documentation.',
    description: 'The ultimate eight-hour elite maternity experience providing complete documentation of your pregnancy journey. This comprehensive full-day session ensures thorough coverage with extensive styling, multiple locations, partner portraits, and complete creative freedom. Perfect for expecting mothers who want the most comprehensive maternity portrait collection with unlimited time and creative possibilities.',
    price: 16000,
    currency: 'R',
    durationMinutes: 480,
    groupSize: 'small',
    isSpecial: false,
  },

  // ============================================
  // NEWBORN CATEGORY
  // ============================================
  {
    templateKey: 'newborn-mini-1hr',
    name: 'Newborn Mini Session',
    category: 'Newborn',
    short: 'Quick newborn session capturing your baby\'s first days.',
    description: 'A gentle one-hour newborn mini session perfect for capturing your baby\'s first precious days. This focused session creates beautiful, natural images of your newborn in a relaxed, comfortable environment. Perfect for new parents who want quality newborn portraits without a long session, ideal for documenting those fleeting early moments.',
    price: 3200,
    currency: 'R',
    durationMinutes: 60,
    groupSize: 'solo',
    isSpecial: false,
  },
  {
    templateKey: 'newborn-classic-2hr',
    name: 'Classic Newborn Portrait',
    category: 'Newborn',
    short: 'Extended newborn session with multiple setups and family portraits.',
    description: 'A beautiful two-hour classic newborn portrait session including multiple setups, family portraits, and sibling combinations. This extended duration allows for feeding breaks, outfit changes, and capturing both posed and lifestyle images. Perfect for families who want a comprehensive newborn portrait collection with time for natural moments and styled portraits.',
    price: 5200,
    currency: 'R',
    durationMinutes: 120,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'newborn-complete-3hr',
    name: 'Complete Newborn Experience',
    category: 'Newborn',
    short: 'Comprehensive newborn session with extensive styling and family coverage.',
    description: 'A complete three-hour newborn experience offering extensive styling, multiple setups, and comprehensive family coverage. This session includes individual baby portraits, family combinations, sibling shots, and detailed styling with props and backdrops. Perfect for families who want a thorough newborn portrait collection with plenty of time for relaxed, natural moments.',
    price: 7200,
    currency: 'R',
    durationMinutes: 180,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'newborn-premium-4hr',
    name: 'Premium Newborn Collection',
    category: 'Newborn',
    short: 'Luxury newborn session with extensive styling and complete coverage.',
    description: 'A premium four-hour newborn collection offering extensive styling, multiple setups, and complete coverage. This luxury session includes individual portraits, family combinations, sibling shots, and detailed styling with props, backdrops, and outfits. Perfect for families who want the most comprehensive newborn portrait collection with unlimited time for creative exploration.',
    price: 9200,
    currency: 'R',
    durationMinutes: 240,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'newborn-luxury-6hr',
    name: 'Luxury Newborn Day',
    category: 'Newborn',
    short: 'Full-day newborn experience with unlimited creative possibilities.',
    description: 'A full six-hour luxury newborn day offering unlimited creative possibilities. This extensive session includes extensive styling, multiple setups, family portraits, sibling combinations, and complete coverage of your newborn\'s first portrait experience. Perfect for families who want the most comprehensive newborn portrait collection with plenty of time for feeding breaks, outfit changes, and capturing every precious moment.',
    price: 13000,
    currency: 'R',
    durationMinutes: 360,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'newborn-elite-8hr',
    name: 'Elite Newborn Experience',
    category: 'Newborn',
    short: 'Ultimate full-day newborn coverage with complete documentation.',
    description: 'The ultimate eight-hour elite newborn experience providing complete documentation of your baby\'s first portrait session. This comprehensive full-day session ensures thorough coverage with extensive styling, multiple setups, family portraits, and complete creative freedom. Perfect for families who want the most extensive newborn portrait collection with unlimited time and creative possibilities.',
    price: 16500,
    currency: 'R',
    durationMinutes: 480,
    groupSize: 'small',
    isSpecial: false,
  },

  // ============================================
  // EVENT CATEGORY
  // ============================================
  {
    templateKey: 'event-express-1hr',
    name: 'Event Express',
    category: 'Event',
    short: 'Quick event coverage for small gatherings and celebrations.',
    description: 'An efficient one-hour event photography session perfect for small gatherings, celebrations, or quick documentation needs. This focused session captures the key moments and highlights of your event, ideal for corporate functions, small parties, or intimate celebrations where you want professional coverage without a long commitment.',
    price: 3000,
    currency: 'R',
    durationMinutes: 60,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'event-coverage-2hr',
    name: 'Event Coverage',
    category: 'Event',
    short: 'Complete event coverage with key moments and highlights.',
    description: 'A comprehensive two-hour event coverage package capturing all key moments and highlights of your celebration. Perfect for corporate events, parties, or celebrations where you want professional documentation of speeches, presentations, and social moments. This duration ensures I capture the essence of your event while allowing time for candid moments and group photos.',
    price: 5000,
    currency: 'R',
    durationMinutes: 120,
    groupSize: 'medium',
    isSpecial: false,
  },
  {
    templateKey: 'event-complete-3hr',
    name: 'Complete Event Documentation',
    category: 'Event',
    short: 'Full event documentation from start to finish.',
    description: 'A complete three-hour event documentation package covering your celebration from start to finish. This comprehensive coverage captures everything from setup and arrivals to the main event and closing moments. Perfect for corporate events, milestone celebrations, or parties where you want thorough documentation of every important moment and detail.',
    price: 7000,
    currency: 'R',
    durationMinutes: 180,
    groupSize: 'medium',
    isSpecial: false,
  },
  {
    templateKey: 'event-premium-4hr',
    name: 'Premium Event Coverage',
    category: 'Event',
    short: 'Extended event coverage with multiple phases and locations.',
    description: 'A premium four-hour event coverage package offering extended documentation with multiple phases and locations. Perfect for larger events, conferences, or celebrations with multiple components. This duration provides flexibility for travel between locations, capturing different event phases, and ensuring comprehensive coverage of all important moments and details.',
    price: 9000,
    currency: 'R',
    durationMinutes: 240,
    groupSize: 'large',
    isSpecial: false,
  },
  {
    templateKey: 'event-luxury-6hr',
    name: 'Luxury Event Day',
    category: 'Event',
    short: 'Full-day event coverage with complete documentation.',
    description: 'A full six-hour luxury event day package providing complete documentation of your celebration. This extensive coverage ensures thorough documentation of large events, conferences, or multi-phase celebrations. With multiple locations, extensive coverage, and complete creative freedom, I\'ll create a comprehensive visual story that captures every important moment and detail of your event.',
    price: 12500,
    currency: 'R',
    durationMinutes: 360,
    groupSize: 'large',
    isSpecial: false,
  },
  {
    templateKey: 'event-elite-8hr',
    name: 'Elite Event Experience',
    category: 'Event',
    short: 'Ultimate full-day event coverage from setup to breakdown.',
    description: 'The ultimate eight-hour elite event experience providing complete coverage from setup to breakdown. This comprehensive full-day session ensures thorough documentation of large-scale events, conferences, or multi-day celebrations. With multiple locations, extensive coverage, and complete creative freedom, I\'ll deliver a complete visual story that captures every moment, detail, and memory of your event.',
    price: 16000,
    currency: 'R',
    durationMinutes: 480,
    groupSize: 'verylarge',
    isSpecial: false,
  },

  // ============================================
  // BUSINESS CATEGORY
  // ============================================
  {
    templateKey: 'business-express-1hr',
    name: 'Express Business Session',
    category: 'Business',
    short: 'Quick, professional business photography for busy professionals.',
    description: 'An efficient one-hour business photography session designed for busy professionals. Perfect for headshots, LinkedIn photos, or quick product shots. This focused session delivers professional-quality images with minimal time investment, ideal for executives, entrepreneurs, and professionals who need quality business photography without taking half a day out of their schedule.',
    price: 3000,
    currency: 'R',
    durationMinutes: 60,
    groupSize: 'solo',
    isSpecial: false,
  },
  {
    templateKey: 'business-branding-2hr',
    name: 'Business Branding Photography',
    category: 'Business',
    short: 'Professional branding session for your business or personal brand.',
    description: 'A comprehensive two-hour business branding photography session designed to elevate your professional image. Perfect for business leaders, entrepreneurs, and professionals building their personal brand. This session includes multiple looks, settings, and styles to create a cohesive visual identity across your website, social media, and marketing materials. Ideal for those who want professional images that convey confidence and authenticity.',
    price: 5000,
    currency: 'R',
    durationMinutes: 120,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'business-corporate-3hr',
    name: 'Corporate Business Photography',
    category: 'Business',
    short: 'Comprehensive corporate photography for teams and products.',
    description: 'A three-hour corporate business photography session covering team portraits, product photography, and office documentation. Perfect for companies needing professional images for websites, annual reports, or marketing materials. This extended duration allows for multiple setups, team combinations, and detailed product shots, ensuring comprehensive coverage of all your corporate photography needs.',
    price: 7000,
    currency: 'R',
    durationMinutes: 180,
    groupSize: 'medium',
    isSpecial: false,
  },
  {
    templateKey: 'business-premium-4hr',
    name: 'Premium Business Shoot',
    category: 'Business',
    short: 'Extended business photography with multiple setups and locations.',
    description: 'A premium four-hour business photography session offering extensive coverage with multiple setups and locations. Perfect for larger business projects requiring detailed product photography, team portraits, and location documentation. This duration provides flexibility for travel between locations, extensive styling, and capturing a wide variety of images for comprehensive marketing campaigns.',
    price: 9000,
    currency: 'R',
    durationMinutes: 240,
    groupSize: 'medium',
    isSpecial: false,
  },
  {
    templateKey: 'business-luxury-6hr',
    name: 'Luxury Business Day',
    category: 'Business',
    short: 'Full-day business photography with complete project coverage.',
    description: 'A full six-hour luxury business photography day providing complete project coverage. Perfect for large-scale business projects, extensive product catalogs, or comprehensive brand documentation. This extended duration allows for multiple locations, extensive styling changes, and thorough coverage of all project requirements, ensuring every detail is captured to the highest standard.',
    price: 12500,
    currency: 'R',
    durationMinutes: 360,
    groupSize: 'large',
    isSpecial: false,
  },
  {
    templateKey: 'business-elite-8hr',
    name: 'Elite Business Experience',
    category: 'Business',
    short: 'Ultimate full-day business photography with unlimited coverage.',
    description: 'The ultimate eight-hour business photography experience offering unlimited coverage for your most important projects. This comprehensive full-day session ensures complete documentation of large-scale business projects, extensive product lines, or comprehensive brand campaigns. With multiple locations, extensive styling, and complete creative freedom, I\'ll deliver a complete visual solution for your business needs.',
    price: 16000,
    currency: 'R',
    durationMinutes: 480,
    groupSize: 'large',
    isSpecial: false,
  },

  // ============================================
  // LIFESTYLE CATEGORY
  // ============================================
  {
    templateKey: 'lifestyle-casual-1hr',
    name: 'Casual Lifestyle Session',
    category: 'Lifestyle',
    short: 'Relaxed lifestyle photography capturing everyday moments.',
    description: 'A relaxed one-hour casual lifestyle session perfect for capturing everyday moments and authentic connections. Ideal for individuals, couples, or small families who want natural, unposed images that tell their story. This focused session creates beautiful, candid images in a comfortable, natural environment, perfect for those who prefer authentic moments over formal portraits.',
    price: 2800,
    currency: 'R',
    durationMinutes: 60,
    groupSize: 'solo',
    isSpecial: false,
  },
  {
    templateKey: 'lifestyle-story-2hr',
    name: 'Lifestyle Story Session',
    category: 'Lifestyle',
    short: 'Extended lifestyle session telling your daily story.',
    description: 'A beautiful two-hour lifestyle story session that captures your daily life and authentic moments. Perfect for documenting real connections, everyday activities, and genuine emotions. This extended duration allows for multiple locations, outfit changes, and capturing both intimate moments and broader scenes. Ideal for those who want a comprehensive visual story of their lifestyle.',
    price: 4800,
    currency: 'R',
    durationMinutes: 120,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'lifestyle-complete-3hr',
    name: 'Complete Lifestyle Experience',
    category: 'Lifestyle',
    short: 'Comprehensive lifestyle photography with extended coverage.',
    description: 'A complete three-hour lifestyle experience offering comprehensive coverage of your daily life and authentic moments. This session includes multiple locations, various activities, and captures both intimate moments and broader lifestyle scenes. Perfect for families, couples, or individuals who want a thorough documentation of their lifestyle with time for relaxed, natural moments.',
    price: 6800,
    currency: 'R',
    durationMinutes: 180,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'lifestyle-premium-4hr',
    name: 'Premium Lifestyle Collection',
    category: 'Lifestyle',
    short: 'Luxury lifestyle session with extensive coverage and locations.',
    description: 'A premium four-hour lifestyle collection offering extensive coverage with multiple locations and activities. This luxury session is perfect for those who want a comprehensive lifestyle portrait collection. With multiple outfit changes, location variations, and detailed documentation, I\'ll create a stunning gallery that beautifully captures your authentic lifestyle and daily moments.',
    price: 8800,
    currency: 'R',
    durationMinutes: 240,
    groupSize: 'small',
    isSpecial: false,
  },
  {
    templateKey: 'lifestyle-luxury-6hr',
    name: 'Luxury Lifestyle Day',
    category: 'Lifestyle',
    short: 'Full-day lifestyle experience with unlimited creative possibilities.',
    description: 'A full six-hour luxury lifestyle day offering unlimited creative possibilities. This extensive session includes extensive coverage, multiple locations, various activities, and complete documentation of your lifestyle. Perfect for those who want a comprehensive, unhurried lifestyle portrait experience with plenty of time for creative exploration and capturing every authentic moment.',
    price: 12000,
    currency: 'R',
    durationMinutes: 360,
    groupSize: 'medium',
    isSpecial: false,
  },
  {
    templateKey: 'lifestyle-elite-8hr',
    name: 'Elite Lifestyle Experience',
    category: 'Lifestyle',
    short: 'Ultimate full-day lifestyle coverage with complete documentation.',
    description: 'The ultimate eight-hour elite lifestyle experience providing complete documentation of your daily life and authentic moments. This comprehensive full-day session ensures thorough coverage with extensive styling, multiple locations, various activities, and complete creative freedom. Perfect for those who want the most comprehensive lifestyle portrait collection with unlimited time and creative possibilities.',
    price: 15000,
    currency: 'R',
    durationMinutes: 480,
    groupSize: 'medium',
    isSpecial: false,
  },

  // ============================================
  // PROPERTY CATEGORY
  // ============================================
  {
    templateKey: 'property-basic-1hr',
    name: 'Basic Property Session',
    category: 'Property',
    short: 'Quick property photography for listings and documentation.',
    description: 'An efficient one-hour property photography session perfect for real estate listings, property documentation, or quick property showcases. This focused session captures the essential features and spaces of your property, ideal for agents, property owners, or developers who need professional property images quickly.',
    price: 3000,
    currency: 'R',
    durationMinutes: 60,
    groupSize: 'solo',
    isSpecial: false,
  },
  {
    templateKey: 'property-standard-2hr',
    name: 'Standard Property Photography',
    category: 'Property',
    short: 'Comprehensive property photography with multiple rooms and angles.',
    description: 'A comprehensive two-hour property photography session covering multiple rooms, angles, and key features. Perfect for detailed real estate listings, property portfolios, or comprehensive property documentation. This extended duration allows for thorough coverage of all spaces, interior and exterior shots, and capturing the unique character of your property.',
    price: 5000,
    currency: 'R',
    durationMinutes: 120,
    groupSize: 'solo',
    isSpecial: false,
  },
  {
    templateKey: 'property-complete-3hr',
    name: 'Complete Property Documentation',
    category: 'Property',
    short: 'Full property documentation with interior, exterior, and detail shots.',
    description: 'A complete three-hour property documentation session providing thorough coverage of interior, exterior, and detail shots. Perfect for luxury properties, comprehensive listings, or detailed property portfolios. This session includes all rooms, outdoor spaces, architectural details, and key features, ensuring comprehensive documentation of your property.',
    price: 7000,
    currency: 'R',
    durationMinutes: 180,
    groupSize: 'solo',
    isSpecial: false,
  },
  {
    templateKey: 'property-premium-4hr',
    name: 'Premium Property Collection',
    category: 'Property',
    short: 'Luxury property photography with extensive coverage and styling.',
    description: 'A premium four-hour property collection offering extensive coverage with multiple angles, styling, and detailed documentation. Perfect for high-end properties, luxury listings, or comprehensive property portfolios. This luxury session includes extensive interior and exterior coverage, architectural details, lifestyle shots, and complete documentation of your property.',
    price: 9000,
    currency: 'R',
    durationMinutes: 240,
    groupSize: 'solo',
    isSpecial: false,
  },
  {
    templateKey: 'property-luxury-6hr',
    name: 'Luxury Property Day',
    category: 'Property',
    short: 'Full-day property photography with unlimited coverage and styling.',
    description: 'A full six-hour luxury property day offering unlimited coverage and styling for your property. This extensive session includes comprehensive interior and exterior documentation, architectural details, lifestyle shots, multiple angles, and complete property coverage. Perfect for luxury properties, large estates, or properties requiring extensive documentation with plenty of time for creative exploration.',
    price: 12500,
    currency: 'R',
    durationMinutes: 360,
    groupSize: 'solo',
    isSpecial: false,
  },
  {
    templateKey: 'property-elite-8hr',
    name: 'Elite Property Experience',
    category: 'Property',
    short: 'Ultimate full-day property coverage with complete documentation.',
    description: 'The ultimate eight-hour elite property experience providing complete documentation of your property. This comprehensive full-day session ensures thorough coverage with extensive interior and exterior documentation, architectural details, lifestyle shots, multiple angles, and complete creative freedom. Perfect for luxury properties, large estates, or properties requiring the most comprehensive documentation with unlimited time and creative possibilities.',
    price: 16000,
    currency: 'R',
    durationMinutes: 480,
    groupSize: 'solo',
    isSpecial: false,
  },
];
